import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Modal, Pressable, ScrollView, StyleSheet, TouchableOpacity } from 'react-native'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { Text, View } from '../../Themed'
import ExitIcon from '../../Headers/UIButtons'
import { setShowDeliveryModal } from '../../../state/Slices/dataSlice'
import { formStyle, SMALL_WIDTH, smallTextSize } from '../../../constants/GlobalStyle'
import DeliveryPickupToggle from '../DeliveryPickupToggle'
import {
	resetUserLocation,
	setUserLocationLoading,
	setUserState,
} from '../../../state/Slices/userSlice'
import { resetDeliveryEstimate, setPersistedState } from '../../../state/Slices/persistedSlice'
import { AddressCard } from '../AddressCard'
import { showAlert } from '../../../state/Slices/settingsSlice'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import Colors from '../../../constants/Colors'
import AddressForm from '../AddressForm'
import { auth } from '../../../firebase/config'
import { parsePrepTime } from '../../../helpers/prepTimeFunctions'
import { getDeliveryEstimate } from '../../../firebase/fbDataRetrieval/RetrieveLocation'
import { add } from 'date-fns'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { prepText } from '../../../helpers/prepText'
import { PickupTimeBox } from '../../../screens/Checkout/Components/SelectorBox'
import { areAllRestaurantsInCartOpen } from '../../../helpers/restaurantHours'
import ScheduleOrder from '../../ScheduleHours/ScheduleOrderModal'
import Banner from '../../Banner'
import { INVALID_ADDRESS_TEXT } from '../../../screens/Checkout/Components/UI'

interface DeliveryDetailsModalProps {
	closeModal: () => void
}

// REFACTOR with NewAddressModal.tsx
export default function DeliveryDetailsModal({ closeModal }: DeliveryDetailsModalProps) {
	const dispatch = useAppDispatch()
	const { userData, userLocation } = useAppSelector(state => state.user)
	const { contactlessDropoff, activeRID, cart, cartPrep, isDelivery } = useAppSelector(
		state => state.persisted
	)
	const { restaurants, restaurantList, userCurrentLocation, loadedData } = useAppSelector(
		state => state.data
	)
	const { delivery, allowOrdersOutsideOrgHours, localTimezone } = useAppSelector(
		state => state.settings
	)
	const isLoggedIn = !!auth.currentUser
	const [saveAddress, setSaveAddress] = useState(isLoggedIn)
	const [isLoading, setIsLoading] = useState(false)
	const [showInvalidAddress, setShowInvalidAddress] = useState(false)
	const [showNewAddressForm, setShowNewAddressForm] = useState(false)
	const [visible, setVisible] = useState(true)

	const hoursRef = useRef<any>()

	const deliveryAddresses = useMemo(() => {
		const addresses = []

		if (isLoggedIn) {
			if (userData?.user?.deliveryAddresses) addresses.push(...userData.user.deliveryAddresses)
		} else {
			if (userLocation?.longAddress) addresses.push(userLocation)
		}

		return addresses.sort((a, b) => a.longAddress.localeCompare(b.longAddress))
	}, [userData?.user?.deliveryAddresses, userLocation])

	const userId = isLoggedIn ? auth.currentUser?.uid : ''
	const prepTime = loadedData[activeRID]?.prepTime ?? 15

	const functions = getFunctions()
	const setDeliveryAddressForUser = httpsCallable(functions, 'v2_setDeliveryAddressForUser')

	const handleDeliveryEstimate = async (userAddress: string) => {
		setIsLoading(true)
		dispatch(setUserLocationLoading(true))
		try {
			const estimate: any = await getDeliveryEstimate(
				userAddress,
				add(new Date(), parsePrepTime('P0DT0H' + prepTime + 'M0S')),
				delivery.isSandbox
			)

			dispatch(
				setPersistedState({
					deliveryEstimate: {
						restaurantId: global.org,
						userAddress: userAddress,
						estimate: {
							fee: splitFee(estimate?.fee) || -1,
							timeToDeliver: estimate?.timeToDeliver || -1,
							totalFee: estimate?.fee || -1,
							gst: estimate?.gst,
							pst: estimate?.pst,
							deliveryTax:
								estimate?.originalDoorDashTaxComponents?.reduce(
									(sum, comp) => sum + comp.amount,
									0
								) ?? 0,
						},
					},
				})
			)

			if (estimate === 400) {
				setShowInvalidAddress(true)
			} else {
				setShowInvalidAddress(false)
				dispatch(setShowDeliveryModal(false))
			}
		} catch (error) {
			console.error('Error processing estimate', error)
		}
		setIsLoading(false)
		dispatch(setUserLocationLoading(false))
	}

	const handleAddressSelect = useCallback(
		async address => {
			const locationObj = {
				coords: null,
				formattedAddress: address.formattedAddress,
				longAddress: address.longAddress,
				label: address.label,
				instructions: address.instructions,
				updatedAt: address.updatedAt,
				id: address.id,
				addressObject: address.addressObject,
			}
			dispatch(setUserState({ userLocation: locationObj }))
			setDeliveryAddressForUser({
				userId: userId,
				address: locationObj,
				orgId: global.org,
				isEdit: false,
				isDelete: false,
			})
			setShowInvalidAddress(false)
			await handleDeliveryEstimate(address.longAddress)
		},
		[dispatch, handleDeliveryEstimate]
	)

	function splitFee(fee) {
		if (delivery.orgCoverageType === 'percent') {
			return fee * (delivery.orgCoverage / 100)
		} else if (fee - delivery.orgCoverage >= 0) {
			return fee - delivery.orgCoverage
		} else {
			return 0
		}
	}

	async function handleDeleteAddress(address: any) {
		setVisible(false)
		await setDeliveryAddressForUser({
			userId: userId,
			address: address,
			orgId: global.org,
			isEdit: false,
			isDelete: true,
		})

		if (address.id === userLocation.id && address.longAddress === userLocation.longAddress) {
			dispatch(resetUserLocation())
			dispatch(resetDeliveryEstimate())
		}
		setVisible(true)
	}

	const cartOpen = areAllRestaurantsInCartOpen(
		cart,
		restaurants,
		loadedData[global.org].hours ?? null,
		allowOrdersOutsideOrgHours,
		localTimezone
	)
	const filteredRestaurantsByItems = restaurantList.filter(restaurant =>
		cart.some(item => item.rId === restaurant.id)
	)

	return (
		<>
			<Modal
				visible={visible}
				statusBarTranslucent={true}
				transparent={true}
				animationType="fade"
				style={{ zIndex: 0 }}
			>
				<View style={styles.modalOverlay}>
					<View style={styles.modalContent}>
						<ScrollView showsVerticalScrollIndicator={false}>
							<ExitIcon
								style={{ position: 'absolute', right: 0, top: 0 }}
								color="black"
								onPress={() => {
									closeModal()
								}}
								removeShadow
							/>
							<View style={[formStyle.headerContainer, styles.headerContainer]}>
								<Text style={[formStyle.headerText, styles.headerText]}>Delivery details</Text>
								<DeliveryPickupToggle showAddress={false} />
							</View>
							{showInvalidAddress && (
								<View style={{ marginTop: 6 }}>
									<Banner text={INVALID_ADDRESS_TEXT} />
								</View>
							)}

							{isDelivery &&
								(showNewAddressForm ? (
									<View style={styles.container}>
										<Text style={[styles.subheaderText, { marginBottom: 8 }]}>
											Delivery address
										</Text>
										<AddressForm
											userCurrentLocation={userCurrentLocation}
											handleDeliveryEstimate={handleDeliveryEstimate}
											saveAddress={saveAddress}
											setSaveAddress={setSaveAddress}
											isLoading={isLoading}
											setIsLoading={setIsLoading}
											userId={userId}
											closeCallback={() => setShowNewAddressForm(false)}
										/>
									</View>
								) : (
									<>
										<View style={styles.container}>
											<Text style={styles.subheaderText}>Delivery address</Text>
											{deliveryAddresses.map((address, index) => (
												<AddressCard
													key={index}
													truncateLength={SMALL_WIDTH ? 38 : null}
													longAddress={address.longAddress}
													label={address?.label}
													instructions={address?.instructions}
													onPress={() => handleAddressSelect(address)}
													onDelete={() => {
														setVisible(false)

														dispatch(
															showAlert({
																title: 'Delete saved address?',
																body: '',
																buttonA: 'Cancel',
																buttonB: 'Delete',
																onPressB: async () => {
																	handleDeleteAddress(address)
																},
																onPressA: () => {
																	dispatch(setShowDeliveryModal(true))
																},
															})
														)
													}}
												/>
											))}
											<TouchableOpacity
												onPress={() => {
													setShowNewAddressForm(true)
												}}
												style={{
													display: 'flex',
													flexDirection: 'row',
													alignItems: 'center',
													gap: 8,
													paddingHorizontal: 3,
													marginTop: 10,
												}}
											>
												<MaterialCommunityIcons name={'plus'} size={20} color={'#000'} />
												<Text
													style={{
														fontSize: smallTextSize,
														fontWeight: '600',
														color: Colors.greyscale[6],
													}}
												>
													Enter new address
												</Text>
											</TouchableOpacity>
										</View>

										<View style={styles.container}>
											<Text style={[styles.subheaderText, { marginBottom: 8 }]}>Options</Text>
											<TouchableOpacity
												onPress={() =>
													dispatch(setPersistedState({ contactlessDropoff: !contactlessDropoff }))
												}
												style={[
													styles.dropoffToggle,
													{ marginLeft: showNewAddressForm ? '2.5%' : 0 },
												]}
											>
												<MaterialCommunityIcons
													name={
														contactlessDropoff
															? 'checkbox-marked-circle'
															: 'checkbox-blank-circle-outline'
													}
													size={26}
													color={contactlessDropoff ? global.orgColor : '#818181'}
												/>
												<Text style={styles.dropoffToggleText}>Contactless dropoff</Text>
											</TouchableOpacity>
											{cart.length > 0 && (
												<View style={[styles.dropoffToggle, { marginLeft: 0 }]}>
													<MaterialCommunityIcons name={'clock-outline'} size={26} />
													<View
														style={{
															display: 'flex',
															flexDirection: 'row',
															justifyContent: 'flex-start',
															width: '100%',
														}}
													>
														<Text
															style={{
																fontSize: smallTextSize,
																fontWeight: '700',
																marginLeft: 10,
																width: 'auto',
															}}
														>
															{isDelivery ? 'Delivery' : 'Pickup'} time:{' '}
														</Text>
														<PickupTimeBox
															toggleTimeModal={() => {
																hoursRef.current.open()
															}}
															pickupText={prepText(cartPrep, isDelivery)}
															isOpen={cartOpen}
															displayAsOption
														/>
														{filteredRestaurantsByItems && <ScheduleOrder hoursRef={hoursRef} />}
													</View>
												</View>
											)}
										</View>
									</>
								))}

							{!isDelivery && (
								<View style={[styles.dropoffToggle, { marginLeft: 0, marginBottom: 10 }]}>
									<MaterialCommunityIcons name={'clock-outline'} size={26} />
									<View
										style={{
											display: 'flex',
											flexDirection: 'row',
											justifyContent: 'flex-start',
											width: '100%',
										}}
									>
										<Text
											style={{
												fontWeight: '700',
												marginLeft: 10,
												width: 'auto',
											}}
										>
											{isDelivery ? 'Delivery' : 'Pickup'} time:{' '}
										</Text>
										<PickupTimeBox
											toggleTimeModal={() => {
												hoursRef.current.open()
											}}
											pickupText={prepText(cartPrep, isDelivery)}
											isOpen={cartOpen}
											displayAsOption
										/>
										{filteredRestaurantsByItems && <ScheduleOrder hoursRef={hoursRef} />}
									</View>
								</View>
							)}
							<Pressable
								style={[
									formStyle.button,
									{ backgroundColor: global.orgColor },
									showNewAddressForm && { width: '99%', alignSelf: 'center' },
								]}
								onPress={() => {
									showNewAddressForm ? setShowNewAddressForm(false) : closeModal()
								}}
							>
								<Text style={[formStyle.btnText, { textTransform: 'uppercase' }]}>
									{showNewAddressForm ? 'Back' : 'Continue'}
								</Text>
							</Pressable>
						</ScrollView>
					</View>
				</View>
			</Modal>
		</>
	)
}

const styles = StyleSheet.create({
	modalOverlay: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: 'rgba(0,0,0,0.5)',
		paddingHorizontal: '2.5%',
	},
	modalContent: {
		backgroundColor: 'white',
		padding: 20,
		width: '100%',
		maxWidth: 500,
		borderRadius: 10,
		shadowOpacity: 0.25,
		shadowRadius: 3.84,
		elevation: 5,
		maxHeight: '90%',
	},
	headerContainer: {
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 16,
		border: 0,
		paddingVertical: 0,
		paddingHorizontal: 0,
	},
	headerText: {
		fontSize: 28,
	},
	subheaderText: {
		fontSize: 18,
		fontWeight: 'bold',
		color: '#333',
	},
	container: {
		marginVertical: 16,
	},
	dropoffToggle: {
		flexDirection: 'row',
		alignItems: 'center',
		marginHorizontal: 0,
		marginVertical: 10,
	},
	dropoffToggleText: {
		flex: 1,
		fontSize: smallTextSize,
		fontWeight: '700',
		marginLeft: 10,
	},
})
